import { t } from "@lingui/macro";
import { useUpdateTimeTrackingRatesForBrandMutation } from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { assert } from "@src/utils/types";
import { observer, useLocalObservable } from "mobx-react-lite";
import Router from "next/router";
import { useContext } from "react";
import { CardTimeTrackingRate } from "../../projects/settings/CardTimeTrackingRate";
import { TimeTrackingRate } from "../../projects/settings/TimeTrackingRate";
import { PageBrandDetailCtx } from "./PageBrandDetail";

export const CardHourlyRates = observer(function CardHourlyRates() {
  const appStore = useStore();
  const store = useContext(PageBrandDetailCtx);
  const { brand } = store;

  assert(brand);

  const state = useLocalObservable(() => ({
    average_time_tracking_rate: NumberModel.fromDenomValue(
      brand.average_time_tracking_rate ?? 0,
      appStore.workspaceStore.settings?.currency.denomination ?? 2,
    ),
    calculatedTimeTrackingRate: NumberModel.fromDenomValue(
      brand.calculated_average_time_tracking_rate ?? 0,
      appStore.workspaceStore.settings?.currency.denomination ?? 2,
    ),

    saveForWorkType: (id: string, val: NumberModel) => {
      update({
        variables: {
          input: {
            brand_id: brand.id,
            time_tracking_rates_per_work_type: [
              { rate: val.denominatedValue, time_tracking_work_type_id: id },
            ],
          },
        },
      }).finally(() =>
        appStore.UIStore.toast({ title: t`Saved`, status: "success" }),
      );
    },

    saveForAverage: (val: NumberModel) => {
      state.average_time_tracking_rate = val;

      update({
        variables: {
          input: {
            brand_id: brand.id,
            average_rate:
              val.denominatedValue > 0 ? val.denominatedValue : null,
          },
        },
      }).finally(() =>
        appStore.UIStore.toast({ title: t`Saved`, status: "success" }),
      );
    },
  }));

  const [update] = useUpdateTimeTrackingRatesForBrandMutation({
    ...onError(),
  });

  return (
    <CardTimeTrackingRate
      bordered={false}
      onEdit={() =>
        Router.push(`/brands/hourly-rates-and-commissions/?id=${brand.id}`)
      }
      average_time_tracking_rate={state.average_time_tracking_rate}
      calculatedTimeTrackingRate={state.calculatedTimeTrackingRate}
      onAverageTimeTrackingRateChange={state.saveForAverage}
    >
      {!(
        appStore.workspaceStore.settings?.moduleSettings.budgetovac &&
        can("integration_budgetovac")
      ) &&
        brand?.timeTrackingRates.map((r) => (
          <TimeTrackingRate
            key={r.timeTrackingWorkType.id}
            rate={r}
            onRateChange={state.saveForWorkType}
          />
        ))}
    </CardTimeTrackingRate>
  );
});
